import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Alert,
  TouchableOpacity,
  Image,
  ScrollView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { RootStackParamList } from '../types/navigation';
import { Button } from '../components/common';
import * as blueskyService from '../services/bluesky';
import { detectFacets, countGraphemes } from '../utils/text';
import { saveDraft, loadDraft, clearDraft } from '../utils/draft';
import { colors } from '../utils/colors';
import { typography } from '../utils/typography';
import { spacing } from '../utils/theme';

type Props = NativeStackScreenProps<RootStackParamList, 'Compose'>;

const MAX_POST_LENGTH = 300;

export const ComposeScreen: React.FC<Props> = ({ navigation, route }) => {
  const { replyTo, quoteTo } = route.params || {};
  const [text, setText] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [images, setImages] = useState<string[]>([]);

  // 下書きを読み込む
  useEffect(() => {
    const loadSavedDraft = async () => {
      if (!replyTo && !quoteTo) {
        const draft = await loadDraft();
        if (draft && !draft.replyTo) {
          setText(draft.text);
          setImages(draft.images || []);
        }
      }
    };
    loadSavedDraft();
  }, [replyTo, quoteTo]);

  // 自動保存（テキストまたは画像が変更されたら3秒後に保存）
  useEffect(() => {
    if (!replyTo && !quoteTo && (text.trim() || images.length > 0)) {
      const timer = setTimeout(() => {
        saveDraft({
          text,
          images,
          createdAt: new Date().toISOString(),
        });
      }, 3000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [text, images, replyTo, quoteTo]);

  const characterCount = countGraphemes(text);
  const isOverLimit = characterCount > MAX_POST_LENGTH;
  const canPost = text.trim().length > 0 && !isOverLimit && !isPosting;

  const pickImage = async () => {
    if (images.length >= 4) {
      Alert.alert('エラー', '画像は最大4枚までです');
      return;
    }

    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('エラー', 'カメラロールへのアクセス権限が必要です');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsMultipleSelection: true,
      quality: 0.8,
      selectionLimit: 4 - images.length,
    });

    if (!result.canceled && result.assets) {
      const newImages = result.assets.map(asset => asset.uri);
      setImages(prev => [...prev, ...newImages].slice(0, 4));
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handlePost = async () => {
    if (!canPost) return;

    try {
      setIsPosting(true);

      // ファセット（メンション、URL、ハッシュタグ）を検出
      const facets = await detectFacets(text);

      if (replyTo) {
        // リプライの場合
        await blueskyService.createReply(
          text,
          replyTo.uri,
          replyTo.cid,
          replyTo.rootUri || replyTo.uri,
          replyTo.rootCid || replyTo.cid,
          facets
        );
      } else if (quoteTo) {
        // 引用投稿の場合
        await blueskyService.createQuotePost(
          text,
          quoteTo.uri,
          quoteTo.cid,
          facets
        );
      } else {
        // 通常投稿
        await blueskyService.createPost(text, facets);
      }

      // 下書きを削除して自動的に閉じる
      clearDraft();
      navigation.goBack();
    } catch (error) {
      console.error('Post error:', error);
      Alert.alert('エラー', '投稿に失敗しました');
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        {/* ヘッダー */}
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.closeButton}>
            <Ionicons name="close" size={28} color={colors.text} />
          </TouchableOpacity>
          <Button
            title={isPosting ? '投稿中...' : '投稿'}
            onPress={handlePost}
            disabled={!canPost}
            loading={isPosting}
            size="small"
          />
        </View>

        {/* リプライ先表示 */}
        {replyTo && (
          <View style={styles.replyToContainer}>
            <Ionicons name="return-down-forward" size={16} color={colors.textSecondary} />
            <Text style={styles.replyToText}>
              @{replyTo.authorHandle} への返信
            </Text>
          </View>
        )}

        {/* 引用投稿先表示 */}
        {quoteTo && (
          <View style={styles.replyToContainer}>
            <Ionicons name="chatbox-outline" size={16} color={colors.textSecondary} />
            <Text style={styles.replyToText}>
              投稿を引用
            </Text>
          </View>
        )}

        {/* 投稿入力 */}
        <ScrollView style={styles.content}>
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder={replyTo ? '返信を入力...' : quoteTo ? '引用コメントを入力...' : '今何してる？'}
              placeholderTextColor={colors.textSecondary}
              multiline
              value={text}
              onChangeText={setText}
              autoFocus
            />
          </View>

          {/* 画像プレビュー */}
          {images.length > 0 && (
            <View style={styles.imagesContainer}>
              {images.map((uri, index) => (
                <View key={index} style={styles.imageWrapper}>
                  <Image source={{ uri }} style={styles.imagePreview} />
                  <TouchableOpacity
                    style={styles.removeImageButton}
                    onPress={() => removeImage(index)}
                  >
                    <Ionicons name="close-circle" size={24} color={colors.background} />
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          )}

          {/* 文字数カウンター */}
          <View style={styles.footer}>
            <TouchableOpacity
              style={styles.imageButton}
              onPress={pickImage}
              disabled={images.length >= 4}
            >
              <Ionicons
                name="image-outline"
                size={24}
                color={images.length >= 4 ? colors.textSecondary : colors.primary}
              />
              {images.length > 0 && (
                <Text style={styles.imageCount}>{images.length}/4</Text>
              )}
            </TouchableOpacity>

            <Text
              style={[
                styles.characterCount,
                isOverLimit && styles.characterCountOver,
              ]}
            >
              {characterCount}/{MAX_POST_LENGTH}
            </Text>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  keyboardView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  closeButton: {
    padding: spacing.xs,
  },
  replyToContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.backgroundSecondary,
    gap: spacing.xs,
  },
  replyToText: {
    ...typography.bodySmall,
    color: colors.textSecondary,
  },
  content: {
    flex: 1,
  },
  inputContainer: {
    padding: spacing.md,
  },
  input: {
    ...typography.bodyLarge,
    color: colors.text,
    textAlignVertical: 'top',
    minHeight: 120,
  },
  imagesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: spacing.md,
    gap: spacing.sm,
  },
  imageWrapper: {
    position: 'relative',
    width: '48%',
    aspectRatio: 1,
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
  },
  removeImageButton: {
    position: 'absolute',
    top: spacing.xs,
    right: spacing.xs,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 12,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  imageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  imageCount: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  characterCount: {
    ...typography.bodySmall,
    color: colors.textSecondary,
  },
  characterCountOver: {
    color: colors.error,
    fontWeight: '700',
  },
});
